/**
 * Path-based router. The service worker and .htaccess both resolve every path
 * to index.html, so deep links work without a hash.
 */

const routes = []
let onChange = () => {}

export function defineRoutes(definitions, handler) {
  routes.length = 0
  routes.push(...definitions)
  onChange = handler
}

export function currentPath() {
  return window.location.pathname.replace(/\/+$/, '') || '/'
}

export function match(path = currentPath()) {
  for (const route of routes) {
    const result = route.pattern.exec(path)

    if (result !== null) {
      return { ...route, params: result.slice(1) }
    }
  }

  return null
}

/**
 * The last path we actually rendered.
 *
 * Overlays push a history entry of their own so the back gesture closes them
 * instead of leaving the screen (NAVIGATION.md §3). Those entries carry the same
 * path, so a popstate that lands where we already are belongs to an overlay, not
 * to us: re-rendering would repaint the screen and throw away its scroll
 * position for nothing.
 */
let lastPath = currentPath()

export function navigate(path, { replace = false } = {}) {
  if (path === currentPath()) {
    return
  }

  window.history[replace ? 'replaceState' : 'pushState']({}, '', path)
  lastPath = currentPath()
  onChange()
}


/**
 * Attaches navigation listeners. It deliberately does not render: the first
 * paint is the caller's job, after the session is known.
 */
export function startRouter() {
  window.addEventListener('popstate', () => {
    const path = currentPath()

    if (path === lastPath) {
      return
    }

    lastPath = path
    onChange()
  })

  // Intercept in-app links so navigation never reloads the shell.
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-link]')

    if (link === null || event.metaKey || event.ctrlKey || event.shiftKey) {
      return
    }

    event.preventDefault()
    navigate(link.getAttribute('href'))
  })
}
